import {TableTheme, TableWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {csvTableService, Icon, IconName, isModPressed} from '@dv5/components'
import {useTableWidgetUpdate} from "./useTableWidgetUpdate"
import {useAutoSave} from "../../hooks/useAutoSave"
import {TABLE_THEME_COLORS, TABLE_THEME_LABELS} from "./constants"
import './TableWidgetFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: TableWidgetData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const TableWidgetFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const [editingText, setEditingText] = useState(data.text)
    const [editingTheme, setEditingTheme] = useState<TableTheme>(data.theme)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const themeDropdownRef = useRef<HTMLDivElement>(null)
    const [cursorPosition, setCursorPosition] = useState<{ row: number; col: number }>({ row: 0, col: 0 })
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text)
        setEditingTheme(data.theme)
    }, [data.text, data.theme])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (themeDropdownRef.current && !themeDropdownRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const {updateData} = useTableWidgetUpdate({canvasId, widgetId})

    const saveText = () => {
        if (editingText !== data.text || editingTheme !== data.theme) {
            updateData(['text', 'theme'], {...data, text: editingText, theme: editingTheme})
        }
    }

    useAutoSave({saveFn: saveText})

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }

    const handleThemeChange = (theme: TableTheme) => {
        setEditingTheme(theme)
    }

    const handleAddRow = () => {
        setEditingText(csvTableService.getAfterAddRow(editingText))
    }

    const handleAddColumn = () => {
        setEditingText(csvTableService.getAfterAddColumn(editingText))
    }

    const handleDuplicateRow = () => {
        setEditingText(csvTableService.getAfterDuplicateRow(editingText, cursorPosition?.row ?? 0))
    }

    const handleDeleteRow = () => {
        setEditingText(csvTableService.getAfterDeleteRow(editingText, cursorPosition?.row ?? 0))
    }

    // Handle textarea selection
    const handleSelect = () => {
        const el = textAreaRef.current
        if (!el) {
            return
        }
        if (setCursorPosition) {
            const beforeCaret = editingText.slice(0, el.selectionStart)
            const rows = beforeCaret.split('\n')
            const row = rows.length - 1
            const col = rows[rows.length - 1].length
            setCursorPosition({ row, col })
        }
    }

    return (
        <div className='table-widget-full-editor'>
            <div className='table-widget-full-editor__header'>
                <span className='table-widget-full-editor__title'>Table Widget Properties</span>
                <button
                    className='table-widget-full-editor__close'
                    onClick={onClose}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='table-widget-full-editor__sections'>
                <div className='table-widget-full-editor__section table-widget-full-editor__section--theme' title='Theme'>
                    <div className='table-widget-full-editor__theme-dropdown' ref={themeDropdownRef}>
                        <button
                            className='table-widget-full-editor__theme-dropdown-toggle'
                            type='button'
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <span>{TABLE_THEME_LABELS[editingTheme]}</span>
                            <span className='table-widget-full-editor__theme-dropdown-arrow'>▼</span>
                        </button>
                        {isDropdownOpen && (
                            <div className='table-widget-full-editor__theme-dropdown-panel'>
                                {Object.values(TableTheme).map(theme => (
                                    <div
                                        key={theme}
                                        className={`table-widget-full-editor__theme-dropdown-item ${editingTheme === theme ? 'table-widget-full-editor__theme-dropdown-item--active' : ''}`}
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            handleThemeChange(theme)
                                            setIsDropdownOpen(false)
                                        }}
                                        role='option'
                                        aria-selected={editingTheme === theme}
                                    >
                                        <span
                                            className='table-widget-full-editor__theme-dropdown-swatch'
                                            style={{backgroundColor: TABLE_THEME_COLORS[theme]}}
                                        />
                                        {TABLE_THEME_LABELS[theme]}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className='table-widget-full-editor__csv-toolbar'>
                    <button
                        className='table-widget-full-editor__toolbar-btn'
                        onClick={handleAddRow}
                        aria-label='Add row'
                    >
                        <Icon name={IconName.AddRow} className='table-widget-full-editor__toolbar-icon' />
                        Add row
                    </button>
                    <button
                        className='table-widget-full-editor__toolbar-btn'
                        onClick={handleAddColumn}
                        aria-label='Add column'
                    >
                        <Icon name={IconName.Add} className='table-widget-full-editor__toolbar-icon' />
                        Add column
                    </button>
                    <button
                        className='table-widget-full-editor__toolbar-btn'
                        onClick={handleDuplicateRow}
                        aria-label='Duplicate row'
                    >
                        <Icon name={IconName.Copy} className='table-widget-full-editor__toolbar-icon' />
                        Duplicate row
                    </button>
                    <button
                        className='table-widget-full-editor__toolbar-btn'
                        onClick={handleDeleteRow}
                        aria-label='Delete row'
                    >
                        <Icon name={IconName.Delete} className='table-widget-full-editor__toolbar-icon' />
                        Delete row
                    </button>
                </div>

                <div className='table-widget-full-editor__section table-widget-full-editor__section--text' title='CSV Source'>
                    <textarea
                        className='table-widget-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        onSelect={handleSelect}
                        ref={textAreaRef}
                        autoFocus
                        rows={10}
                    />
                </div>
            </div>
        </div>
    )
}
